import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Avatar,
  Chip,
  IconButton,
  Divider,
  Stack
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PhoneIcon from '@mui/icons-material/Phone';
import { useNavigate } from 'react-router-dom';

const CustomerTable = ({ customers, isMobile }) => {
  const navigate = useNavigate();

  const handleRowClick = (id) => {
    navigate(`/edit-customer/${id}`);
  };

  if (isMobile) {
    if (!customers || customers.length === 0) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" py={6}>
          <Typography variant="body1" color="text.secondary">
            No customers found
          </Typography>
        </Box>
      );
    }

    return (
      <Box display="flex" flexDirection="column" gap={2} px={1}>
        {customers.map((row, index) => (
          <Paper
            key={row.id}
            elevation={1}
            sx={{
              p: 2,
              borderRadius: 2,
              cursor: 'pointer',
              transition: '0.2s',
              '&:hover': {
                boxShadow: 3,
                bgcolor: 'action.hover'
              }
            }}
            onClick={() => handleRowClick(row.id)}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                src={row.profile_image}
                alt={row.name}
                sx={{ width: 48, height: 48 }}
              />

              <Box flex={1}>
                <Typography fontWeight={600} variant="body1">
                  {row.name}
                </Typography>

                <Chip
                  size="small"
                  label={row.status}
                  color={row.status === 'Active' ? 'success' : 'default'}
                  sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                />
              </Box>
            </Stack>

            <Divider sx={{ my: 1.5 }} />

            <Stack spacing={1}>
              <InfoRow
                icon={<PhoneIcon fontSize="small" />}
                value={`+${row.country_code} ${row.mobile_number}`}
              />

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Customer #{index + 1}
              </Typography>
            </Stack>

            <Divider sx={{ my: 1.5 }} />

            <Box display="flex" justifyContent="flex-end">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRowClick(row.id);
                }}
                sx={{ p: 0.5 }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
        ))}
      </Box>
    );
  }

  return (
    <TableContainer elevation={1} sx={{ borderRadius: 2 }}>
      <Table size="small" sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow sx={{ bgcolor: 'background.default' }}>
            <TableCell sx={{ py: 1, fontWeight: 600, fontSize: '0.875rem' }}>#</TableCell>
            <TableCell sx={{ py: 1, fontWeight: 600, fontSize: '0.875rem' }}>Profile</TableCell>
            <TableCell sx={{ py: 1, fontWeight: 600, fontSize: '0.875rem' }}>Name</TableCell>
            <TableCell sx={{ py: 1, fontWeight: 600, fontSize: '0.875rem' }}>Mobile</TableCell>
            <TableCell sx={{ py: 1, fontWeight: 600, fontSize: '0.875rem' }}>Status</TableCell>
            <TableCell align="right" sx={{ py: 1, fontWeight: 600, fontSize: '0.875rem' }}>Action</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {!customers || customers.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No customers found
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            customers.map((row, index) => (
              <TableRow
                key={row.id}
                hover
                sx={{
                  cursor: 'pointer',
                  '&:last-child td, &:last-child th': { border: 0 },
                  '&:hover': { bgcolor: 'action.hover' }
                }}
                onClick={() => handleRowClick(row.id)}
              >
                <TableCell sx={{ py: 1 }}>{index + 1}</TableCell>

                <TableCell sx={{ py: 1 }}>
                  <Avatar 
                    src={row.profile_image} 
                    alt={row.name} 
                    sx={{ width: 32, height: 32 }}
                  />
                </TableCell>

                <TableCell sx={{ py: 1 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {row.name}
                  </Typography>
                </TableCell>

                <TableCell sx={{ py: 1 }}>
                  <Typography variant="body2">
                    +{row.country_code} {row.mobile_number}
                  </Typography>
                </TableCell>

                <TableCell sx={{ py: 1 }}>
                  <Chip
                    size="small"
                    label={row.status}
                    color={row.status === 'Active' ? 'success' : 'default'}
                    sx={{ 
                      height: 22, 
                      fontSize: '0.75rem',
                      '& .MuiChip-label': { px: 1 }
                    }}
                  />
                </TableCell>

                <TableCell align="right" sx={{ py: 1 }}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRowClick(row.id);
                    }}
                    sx={{ 
                      p: 0.5,
                      '&:hover': { bgcolor: 'primary.light', color: 'primary.contrastText' }
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const InfoRow = ({ icon, value }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    {icon}
    <Typography variant="body2" fontWeight={500}>
      {value}
    </Typography>
  </Stack>
);

export default CustomerTable;